import React, { useEffect } from "react";
import moment from "moment";
import InputRowGroup from "./Inputs/InputRowGroup";
import InputRow from "./Inputs/InputRow";
import DateInput from "./Inputs/DateInput";
import { handleChange } from "../Utils/DataCollectionUtils";
import { useTranslation } from "react-i18next";
import BooleanInput from "./Inputs/BooleanInput";
import DropdownInput from "./Inputs/DropdownInput";
import TextInput from "./Inputs/TextInput";
import NumberInput from "./Inputs/NumberInput";
import QuoteAllModes from "../Quotes/QuoteAllModes";

import {
    DEPOSIT_FREQUENCIES_FOR_QUOTE,
    isCLBOnlyAllowed,
    isGroupPlanAvailable,
    PLAN_TYPE_IDS,
    PLAN_TYPES,
    yearsToMaturity,
    maxCarryForwardGrantRoom,
    isCompletionInsuranceAllowed,
    isBeneficiaryInsuranceAllowed,
    isCriticalIllnessInsuranceAllowed,
    calculateLifetimeDepositAmount,
    depositModesForPlanTypeTranslated,
    calculateMonthlyDepositAmount,
    incomeWarning,
    isCriticalIllnessInsuranceAllowedForPlanType,
} from "../Utils/PlanUtils";
import {
    displayCurrency,
    displayUnits,
    sprintf,
} from "../Utils/FormattingUtils";
import { calculateMaxEducationSavingsPlans } from "../Utils/KYCUtils";

const FirstPlanDataCollection = ({ state }) => {
    const { i18n } = useTranslation();
    const { t } = useTranslation();
    const [quoting, setQuoting] = React.useState(false);

    const [maturityDescription, setMaturityDescription] = React.useState("");

    //Update the maturity description string based on the maturity date and beneficiary birthdate
    useEffect(() => {
        const beneficiaryAgeInMaturityYear =
            moment(state.plan.maturityDate, "YYYY-MM-DD").year() -
            moment(state.beneficiary.birthdate, "YYYY-MM-DD").year();
        if (
            beneficiaryAgeInMaturityYear < 0 ||
            isNaN(beneficiaryAgeInMaturityYear)
        ) {
            setMaturityDescription(``);
        } else if (beneficiaryAgeInMaturityYear <= 18) {
            const name = state.beneficiary.firstName || "the beneficiary";
            setMaturityDescription(
                sprintf(
                    t("Plan_Mature"),
                    moment(state.plan.maturityDate).format("MMMM Do, YYYY"),
                    name,
                    beneficiaryAgeInMaturityYear,
                ),
            );
        }
    }, [state.plan.maturityDate, state.beneficiary.birthdate]);

    const planOptions = PLAN_TYPES.filter((plan) => {
        if (plan.value === 0) {
            return isGroupPlanAvailable(state);
        }
        return true;
    });

    const depositCodeOptions = Array.from(
        { length: yearsToMaturity(state) },
        (_, i) => i + 1,
    ).map((i) => ({
        label: i,
        value: i,
    }));

    const viewQuote = () => {
        setQuoting(true);
    };

    const stopViewingQuote = () => {
        setQuoting(false);
    };

    if (quoting) {
        // When the Generate Quotes button is pressed, we switch into quote display mode
        return (
            <div>
                <QuoteAllModes
                    state={state}
                    showAllSimultaneously={false}
                    backFunction={stopViewingQuote}
                />
            </div>
        );
    } else {
        //When we're not viewing a quote, we're collecting data for the first plan
        return (
            <form>
                <InputRowGroup label={t("Plan Selection")}>
                    <InputRow label={t("Plan Type")}>
                        <DropdownInput
                            name={"planTypeID"}
                            state={state.plan}
                            onChange={handleChange(state.setPlan)}
                            options={planOptions}
                            numberValue={true}
                        />
                    </InputRow>
                </InputRowGroup>

                {state.plan.planTypeID >= 0 && (
                    <InputRowGroup label={t("Plan Information")}>
                        <InputRow label={t("Deposit Mode")}>
                            <DropdownInput
                                name={"depositMode"}
                                state={state.plan}
                                onChange={handleChange(state.setPlan)}
                                options={depositModesForPlanTypeTranslated(
                                    state.plan.planTypeID,
                                )}
                            />
                        </InputRow>

                        <InputRow label={t("Effective Date")}>
                            <DateInput
                                name={"effectiveDate"}
                                state={state.plan}
                                onChange={handleChange(state.setPlan)}
                            />
                        </InputRow>

                        {state.plan.planTypeID === PLAN_TYPE_IDS.GROUP && (
                            <InputRow label={t("Deposit Code")}>
                                <span>{state.plan.depositCode}</span>
                            </InputRow>
                        )}
                        {state.plan.planTypeID !== PLAN_TYPE_IDS.GROUP && (
                            <InputRow label={t("Deposit Code")}>
                                <DropdownInput
                                    name={"depositCode"}
                                    state={state.plan}
                                    onChange={handleChange(state.setPlan)}
                                    options={depositCodeOptions}
                                />
                            </InputRow>
                        )}
                        <InputRow label={t("Maturity Date")}>
                            <DateInput
                                name={"maturityDate"}
                                state={state.plan}
                                onChange={handleChange(state.setPlan)}
                            />
                        </InputRow>

                        <InputRow>
                            <div className={"centered-text"}>
                                {t(maturityDescription)}
                            </div>
                        </InputRow>

                        {isCLBOnlyAllowed(state) && (
                            <InputRow label={t("CLB Only")}>
                                <BooleanInput
                                    label={t("Yes")}
                                    name={"clbOnly"}
                                    state={state.plan}
                                    onChange={handleChange(state.setPlan)}
                                />
                            </InputRow>
                        )}

                        {state.plan.planTypeID === PLAN_TYPE_IDS.GROUP && (
                            <InputRow label={t("Quote Based On")}>
                                <DropdownInput
                                    name={"quoteBasedOn"}
                                    state={state.quote}
                                    onChange={handleChange(state.setQuote)}
                                    options={[
                                        {
                                            label: t("Number of Units"),
                                            value: "units",
                                        },
                                        {
                                            label: t("Deposit Amount"),
                                            value: "deposit",
                                        },
                                    ]}
                                />
                            </InputRow>
                        )}

                        {state.plan.planTypeID === 0 && (
                            <InputRow label={t("Number of Units")}>
                                {state.quote.quoteBasedOn === "units" && (
                                    <NumberInput
                                        name={"numberOfUnits"}
                                        state={state.plan}
                                        onChange={handleChange(state.setPlan)}
                                        validationRules={{
                                            valueAsNumber: true,
                                            validate: (value) => value >= 0,
                                        }}
                                        disabled={
                                            +state.plan.planTypeID !==
                                            PLAN_TYPE_IDS.GROUP
                                        }
                                    />
                                )}
                                {state.quote.quoteBasedOn === "deposit" && (
                                    <span>
                                        {!isNaN(state.plan.numberOfUnits) &&
                                            displayUnits(
                                                state.plan.numberOfUnits,
                                            )}
                                    </span>
                                )}
                            </InputRow>
                        )}

                        <InputRow
                            label={
                                state.plan.planTypeID ===
                                    PLAN_TYPE_IDS.ACHIEVERS
                                    ? t("Plan Contribution Target")
                                    : t("Deposit Amount")
                            }
                        >
                            {state.quote.quoteBasedOn === "deposit" && (
                                <NumberInput
                                    name={"depositAmount"}
                                    state={state.plan}
                                    onChange={handleChange(state.setPlan)}
                                    validationRules={{
                                        valueAsNumber: true,
                                        validate: (value) => value >= 0,
                                    }}
                                    annotationIcon={"$"}
                                />
                            )}
                            {state.quote.quoteBasedOn === "units" && (
                                <span>
                                    {!isNaN(state.plan.depositAmount) &&
                                        displayCurrency(
                                            state.plan.depositAmount,
                                            i18n.language,
                                            2,
                                        )}{" "}
                                </span>
                            )}

                            {+state.plan.planTypeID !==
                                PLAN_TYPE_IDS.ACHIEVERS && (
                                    <DropdownInput
                                        name={"depositFrequency"}
                                        state={state.quote}
                                        onChange={handleChange(state.setQuote)}
                                        options={DEPOSIT_FREQUENCIES_FOR_QUOTE}
                                    />
                                )}
                        </InputRow>

                        {state.plan.planTypeID === PLAN_TYPE_IDS.ACHIEVERS && (
                            <InputRow label={t("Insurance")}>
                                <span>
                                    {displayCurrency(
                                        calculateLifetimeDepositAmount(
                                            state,
                                            true,
                                        ) -
                                        calculateLifetimeDepositAmount(
                                            state,
                                            false,
                                        ),
                                        i18n.language,
                                        2,
                                    )}
                                </span>
                            </InputRow>
                        )}
                        {state.plan.planTypeID === PLAN_TYPE_IDS.ACHIEVERS && (
                            <InputRow label={t("Total Contributions")}>
                                <span>
                                    {displayCurrency(
                                        calculateLifetimeDepositAmount(
                                            state,
                                            true,
                                        ),
                                        i18n.language,
                                        2,
                                    )}
                                </span>
                            </InputRow>
                        )}

                        {maxCarryForwardGrantRoom(state) > 0 && (
                            <InputRow label={t("Carry Forward Grant Room")}>
                                <NumberInput
                                    label={t("\$")}
                                    name={"grantRoom"}
                                    state={state.plan}
                                    onChange={handleChange(state.setPlan)}
                                    validationRules={{
                                        valueAsNumber: true,
                                        validate: (value) => value >= 0,
                                    }}
                                />
                                <span
                                    className={"info"}
                                >{`${t("Maximum")} $${maxCarryForwardGrantRoom(state)}`}</span>
                            </InputRow>
                        )}

                        {calculateLifetimeDepositAmount(state, false) >
                            50000 && (
                                <InputRow>
                                    <div className={"financial-warning"}>
                                        {t("Lifetime_Limit")}
                                    </div>
                                </InputRow>
                            )}

                        {calculateLifetimeDepositAmount(state, false) <=
                            50000 &&
                            (state.plan.planTypeID === PLAN_TYPE_IDS.GROUP
                                ? calculateMaxEducationSavingsPlans(
                                    state.subscriber,
                                    state.jointSubscriber,
                                    state.knowYourClient,
                                ).monthlyMaximumEducationSavingsPlanGroup
                                : calculateMaxEducationSavingsPlans(
                                    state.subscriber,
                                    state.jointSubscriber,
                                    state.knowYourClient,
                                ).monthlyMaximumEducationSavingsPlan) <
                            calculateMonthlyDepositAmount(state, true) && (
                                <InputRow>
                                    <div className={"financial-warning"}>
                                        {t("KYC_Limit")}
                                    </div>
                                </InputRow>
                            )}

                        {calculateLifetimeDepositAmount(state, false) <=
                            50000 &&
                            (state.plan.planTypeID === PLAN_TYPE_IDS.GROUP
                                ? calculateMaxEducationSavingsPlans(
                                    state.subscriber,
                                    state.jointSubscriber,
                                    state.knowYourClient,
                                ).monthlyMaximumEducationSavingsPlanGroup
                                : calculateMaxEducationSavingsPlans(
                                    state.subscriber,
                                    state.jointSubscriber,
                                    state.knowYourClient,
                                ).monthlyMaximumEducationSavingsPlan) >=
                            calculateMonthlyDepositAmount(state, true) &&
                            incomeWarning(state) && (
                                <InputRow>
                                    <div className={"financial-warning"}>
                                        {t("Affordability_Limit")}
                                    </div>
                                </InputRow>
                            )}
                    </InputRowGroup>
                )}
                {state.plan.planTypeID >= 0 && (
                    <InputRowGroup label={t("Insurance Options")}>
                        {isCompletionInsuranceAllowed(state) &&
                            state.plan.signingProvince != "PQ" && (
                                <InputRow label={t("Completion Insurance")}>
                                    <BooleanInput
                                        label={t("Yes")}
                                        name={"completionInsurance"}
                                        state={state.plan}
                                        onChange={handleChange(state.setPlan)}
                                    />
                                </InputRow>
                            )}

                        {isBeneficiaryInsuranceAllowed(state) && (
                            <InputRow label={t("Beneficiary Insurance")}>
                                <BooleanInput
                                    label={t("Yes")}
                                    name={"nomineeInsurance"}
                                    state={state.plan}
                                    onChange={handleChange(state.setPlan)}
                                />
                            </InputRow>
                        )}

                        {isCriticalIllnessInsuranceAllowedForPlanType(
                            state.plan.planTypeID,
                        ) && (
                                <InputRow label={t("Critical Illness Insurance")}>
                                    {isCriticalIllnessInsuranceAllowed(state) && (
                                        <BooleanInput
                                            label={t("Yes")}
                                            name={"criticalIllnessInsurance"}
                                            state={state.plan}
                                            onChange={handleChange(state.setPlan)}
                                        />
                                    )}

                                    {!isCriticalIllnessInsuranceAllowed(state) && (
                                        <span style={{ fontStyle: "italic" }}>
                                            {t("Completion insurance required")}
                                        </span>
                                    )}
                                </InputRow>
                            )}
                    </InputRowGroup>
                )}

                {state.plan.planTypeID >= 0 && (
                    <InputRowGroup>
                        <InputRow>
                            <button onClick={viewQuote} type="button">
                                {t("Generate Quotation")}
                            </button>
                        </InputRow>
                    </InputRowGroup>
                )}
            </form>
        );
    }
};

export default FirstPlanDataCollection;
